import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  Gift, 
  CheckCircle, 
  XCircle, 
  Crown,
  ArrowRight,
  Loader2
} from "lucide-react";

export default function TrialPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, already_redeemed, invalid, error
  const [message, setMessage] = useState("");
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  
  const code = searchParams.get("code");

  useEffect(() => {
    if (!code) {
      setStatus("invalid");
      setMessage("No trial code provided.");
      return;
    }

    if (!token) {
      // User not logged in - store code and redirect to login with return URL
      // This allows both existing users (who can login) and new users (who can register)
      sessionStorage.setItem('compass_pending_trial_code', code);
      const returnUrl = `/trial?code=${code}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // Attempt to redeem the code
    redeemCode();
  }, [token, code]);

  const redeemCode = async () => {
    try {
      const response = await axios.post(
        `${API}/trial/redeem`,
        { code },
        { withCredentials: true }
      );

      if (response.data.success) {
        setStatus("success");
        setMessage(response.data.message);
        setTrialEndDate(response.data.trial_end_date);
        toast.success("Trial activated!");
      } else {
        setStatus("already_redeemed");
        setMessage(response.data.message);
        setCurrentPlan(response.data.current_plan);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to redeem trial code";
      
      if (errorMsg.includes("already have an active paid subscription")) {
        setStatus("paid_subscriber");
        setMessage("You already have an active paid subscription.");
      } else if (errorMsg.includes("no longer valid") || errorMsg.includes("Invalid")) {
        setStatus("invalid");
        setMessage("This trial link is no longer valid.");
      } else if (errorMsg.includes("already used")) {
        setStatus("already_redeemed");
        setMessage("You have already used a trial.");
      } else {
        setStatus("error");
        setMessage(errorMsg);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b2821] via-[#0d3029] to-[#0b2821] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/5 border-[#a69373]/20 backdrop-blur-sm">
        <CardContent className="p-8">
          {status === "loading" && (
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#a69373] animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Activating Your Trial</h2>
              <p className="text-white/60">Please wait...</p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Trial Activated!</h2>
              <p className="text-white/80 mb-4">{message}</p>
              <div className="bg-[#a69373]/10 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-[#a69373]" />
                  <span className="text-[#a69373] font-semibold">Annual Access</span>
                </div>
                <p className="text-white/60 text-sm">
                  Valid until {formatDate(trialEndDate)}
                </p>
              </div>
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-[#a69373] hover:bg-[#8b7a5e] text-white"
              >
                Explore Everything <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {status === "already_redeemed" && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Trial Already Used</h2>
              <p className="text-white/80 mb-4">{message}</p>
              {currentPlan && (
                <p className="text-white/60 mb-6">
                  Your current plan: <span className="text-[#a69373] font-semibold capitalize">{currentPlan}</span>
                </p>
              )}
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="w-full border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === "paid_subscriber" && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#a69373]/20 flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-[#a69373]" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Already a Subscriber</h2>
              <p className="text-white/80 mb-6">{message}</p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-[#a69373] hover:bg-[#8b7a5e] text-white"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {(status === "invalid" || status === "error") && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Invalid Trial Link</h2>
              <p className="text-white/80 mb-6">{message}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/register")}
                  className="w-full bg-[#a69373] hover:bg-[#8b7a5e] text-white"
                >
                  Create Account
                </Button>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  className="w-full border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10"
                >
                  Sign In
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
